import React, { useState, useEffect } from 'react';
import { Link, useLocation, Outlet, useNavigate } from 'react-router-dom';
import { FiHome, FiLogOut, FiMenu, FiX, FiBell, FiSettings, FiChevronDown, FiSearch, FiArrowLeft,FiMessage } from 'react-icons/fi';
import { FaUserCircle, FaMoneyBillWave, FaNetworkWired, FaCrown } from 'react-icons/fa';
import { AiOutlineIdcard, AiFillStar } from 'react-icons/ai';
import { BsFileEarmarkText, BsBox, BsMoon, BsSun, BsGiftFill } from 'react-icons/bs';
import { HiUserGroup, HiSparkles } from 'react-icons/hi';
import { GiWallet, GiDiamondTrophy } from 'react-icons/gi';
import { BiTransfer } from 'react-icons/bi';
import { motion, AnimatePresence } from 'framer-motion';
import { RiCoinsLine } from 'react-icons/ri';
import { FiBook, FiUsers, FiClipboard } from 'react-icons/fi';
import { BsFileText, BsPersonCheck, BsCalendarEvent } from 'react-icons/bs';
import { HiAcademicCap } from 'react-icons/hi';

const DashboardLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Close sidebar when resizing to larger screens
  useEffect(() => {
    window.scrollTo(0, 0);
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    handleResize(); // Call initially
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Set active submenu based on current path
  useEffect(() => {
    menuItems.forEach((item, index) => {
      if (item.submenu && item.submenu.some(sub => location.pathname === sub.path)) {
        setActiveSubmenu(index);
      }
    });
  }, [location.pathname]);

  const menuItems = [
    { path: '/admin-panel', icon: <FiHome className="w-5 h-5" />, title: 'Dashboard' },
    { path: '/admin-panel/exam-categories', icon: <FiHome className="w-5 h-5" />, title: 'Exam Category' },
    { path: '/admin-panel/tutorial-management', icon: <FiHome className="w-5 h-5" />, title: 'Tutorial Management' },
    { 
      path: '', 
      icon: <FiBell className="w-5 h-5" />, 
      title: 'Notice',
      submenu: [
        { path: '/admin-panel/add-notice', title: 'Add Notice' },
        { path: '/admin-panel/notice-list', title: 'Notice List' }
      ]
    },
    { path: '/admin-panel/membership-plan', icon: <BsPersonCheck className="w-5 h-5" />, title: 'Membership Plan' },
    { 
      path: '', 
      icon: <FiBook className="w-5 h-5" />, 
      title: 'Exam Manage',
      submenu: [
        { path: '/admin-panel/exam-add', title: 'Add Exam' },
        { path: '/admin-panel/exam-list', title: 'Exam List' }
      ]
    },
    { 
      path: '', 
      icon: <BsFileText className="w-5 h-5" />, 
      title: 'Topic Manage',
      submenu: [
        { path: '/admin-panel/topic-add', title: 'Add Topic' },
        { path: '/admin-panel/topic-list', title: 'Topic List' }
      ]
    },
    { 
      path: '', 
      icon: <FiUsers className="w-5 h-5" />, 
      title: 'Student Manage',
      submenu: [
        { path: '/admin-panel/student-add', title: 'Add Student' },
        { path: '/admin-panel/student-list', title: 'Student List' }
      ]
    },
    { 
      path: '', 
      icon: <FiSettings className="w-5 h-5" />, 
      title: 'Marking Rules',
      submenu: [
        { path: '/admin-panel/marking-rules', title: 'View Rules' },
        { path: '/admin-panel/marking-rules/new', title: 'Add Rule' }
      ]
    },
    { path: '/admin-panel/activation-requests', icon: <FiClipboard className="w-5 h-5" />, title: 'Activation Request' },
    { path: '/admin-panel/messages', icon: <FiBell className="w-5 h-5" />, title: 'Contact Requests' },
    { path: '/admin-panel/special-requests', icon: <BsCalendarEvent className="w-5 h-5" />, title: 'Special Batch Request' },
    { path: '/admin-panel/student-results', icon: <HiAcademicCap className="w-5 h-5" />, title: 'Student Result' },
    { path: '/admin-panel/report-transcription-user-lists', icon: <HiUserGroup className="w-5 h-5" />, title: 'Report Transcription User Lists' },
   
  ];

  const toggleSubmenu = (index) => {
    setActiveSubmenu(activeSubmenu === index ? null : index);
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className='flex h-screen bg-gradient-to-br from-blue-50 to-indigo-50'>
      {/* Mobile Sidebar Backdrop */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSidebarOpen(false)}
            className="fixed inset-0 z-20 bg-black bg-opacity-50 lg:hidden"
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.div
        initial={{ x: -300 }}
        animate={{ x: sidebarOpen ? 0 : -300 }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
        className={`fixed inset-y-0 left-0 z-30 w-72 bg-white shadow-xl lg:static lg:translate-x-0 flex flex-col h-full`}
      >
        <div className="flex flex-col h-full">
          {/* Sidebar Header (Mobile) */}
          <div className="flex items-center justify-between p-4 border-b lg:hidden">
            <div className="text-xl font-semibold text-indigo-800">Admin Panel</div>
            <button 
              onClick={() => setSidebarOpen(false)}
              className="p-2 rounded-lg hover:bg-gray-100"
            >
              <FiX className="w-6 h-6 text-gray-600" />
            </button>
          </div>

          {/* Navigation Menu */}
          <nav className="flex-1 px-4 pt-6 pb-4 overflow-y-auto">
            {menuItems.map((item, index) => (
              <motion.div key={item.path} className="mb-2">
                <Link
                  to={item.path}
                  onClick={(e) => {
                    if (item.submenu) {
                      e.preventDefault();
                      toggleSubmenu(index);
                    } else if (window.innerWidth < 1024) {
                      setSidebarOpen(false); // Close sidebar on mobile when clicking a link
                    }
                  }}
                  className={`flex items-center justify-between px-4 py-3 rounded-xl group ${
                    location.pathname === item.path || 
                    (item.submenu && item.submenu.some(sub => location.pathname === sub.path))
                      ? 'bg-indigo-50 text-indigo-800'
                      : 'text-gray-700 hover:bg-indigo-50 hover:text-indigo-800'
                  }`}
                >
                  <div className="flex items-center">
                    <div className="p-1.5 rounded-lg">
                      {item.icon}
                    </div>
                    <span className="ml-3 font-medium">{item.title}</span>
                  </div>
                  {item.submenu && (
                    <FiChevronDown className={`w-4 h-4 transition-transform ${
                      activeSubmenu === index ? 'transform rotate-180' : ''
                    }`} />
                  )}
                </Link>
                
                {/* Submenu */}
                {item.submenu && activeSubmenu === index && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="pl-12 mt-1 space-y-1"
                  >
                    {item.submenu.map(subItem => (
                      <Link
                        key={subItem.path}
                        to={subItem.path}
                        onClick={() => window.innerWidth < 1024 && setSidebarOpen(false)}
                        className={`block px-3 py-2 text-sm rounded-lg ${
                          location.pathname === subItem.path
                            ? 'bg-indigo-100 text-indigo-800'
                            : 'text-gray-600 hover:bg-indigo-50'
                        }`}
                      >
                        {subItem.title}
                      </Link>
                    ))}
                  </motion.div>
                )}
              </motion.div>
            ))}
          </nav>

          {/* Sidebar Footer */}
          <div className="p-4 border-t border-gray-100">
            <button className="flex items-center w-full px-4 py-3 text-gray-600 hover:bg-indigo-50 hover:text-indigo-800 rounded-xl">
              <FiLogOut className="w-5 h-5" />
              <span className="ml-3 font-medium">Logout</span>
            </button>
          </div>
        </div>
      </motion.div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header */}
        <header className="bg-white shadow-sm z-10 fixed w-full">
          <div className="flex items-center justify-between px-4 py-3 lg:px-6">
            <div className="flex items-center">
              <button 
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 mr-2 rounded-lg hover:bg-gray-100 lg:hidden"
              >
                <FiMenu className="w-6 h-6 text-gray-600" />
              </button>
              
              {/* Back button for mobile when needed */}
              {location.pathname !== '/admin-panel' && (
                <button 
                  onClick={handleBack}
                  className="p-2 mr-2 rounded-lg hover:bg-gray-100 lg:hidden"
                >
                  <FiArrowLeft className="w-6 h-6 text-gray-600" />
                </button>
              )}
              
              {/* <h1 className="text-xl font-semibold text-gray-800">Admin Panel</h1> */}
            </div>
            
           
          </div>
        </header>

        {/* Main Content Area */}
        <main className="flex-1 overflow-x-hidden overflow-y-auto pt-16 pb-6 px-4 md:px-6 bg-gray-50">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-sm p-4 md:p-6 border border-gray-100"
            >
              <Outlet />
            </motion.div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;